// client/src/components/OfflineFallback.tsx
// Graceful degradation when offline or chunks fail to load

import React, { useEffect, useState } from 'react';

interface OfflineFallbackProps {
  isOffline: boolean;
  isLoading: boolean;
  error?: Error;
  onRetry?: () => void;
}

export const OfflineFallback: React.FC<OfflineFallbackProps> = ({
  isOffline,
  isLoading,
  error,
  onRetry,
}) => {
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    const handleOnline = () => {
      if (onRetry) onRetry();
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [onRetry]);

  if (!isOffline && !error && !isLoading) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-white z-40 flex items-center justify-center">
      <div className="text-center max-w-md px-4">
        {isOffline && (
          <>
            <div className="text-6xl mb-4">📡</div>
            <h1 className="text-2xl font-bold mb-2">You&apos;re Offline</h1>
            <p className="text-gray-600 mb-6">
              You can still view cached content, but some features may be limited.
            </p>
            <div className="space-y-2">
              <p className="text-sm text-gray-500">Waiting for connection...</p>
              <div className="flex justify-center gap-1">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" />
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
              </div>
            </div>
          </>
        )}

        {error && !isOffline && (
          <>
            <div className="text-6xl mb-4">⚠️</div>
            <h1 className="text-2xl font-bold mb-2">Something Went Wrong</h1>
            <p className="text-gray-600 mb-4">{error.message}</p>
            <div className="space-y-2">
              {isLoading && (
                <p className="text-sm text-gray-500">Attempting recovery...</p>
              )}
              {!isLoading && onRetry && (
                <button
                  onClick={() => {
                    setRetryCount(prev => prev + 1);
                    onRetry();
                  }}
                  className="w-full bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition"
                >
                  Try Again {retryCount > 0 ? `(${retryCount})` : ''}
                </button>
              )}
              <button
                onClick={() => window.location.reload()}
                className="w-full bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300 transition"
              >
                Refresh Page
              </button>
            </div>
          </>
        )}

        {isLoading && !error && (
          <>
            <div className="text-6xl mb-4">⏳</div>
            <h1 className="text-2xl font-bold mb-2">Loading</h1>
            <p className="text-gray-600 mb-6">Please wait while we load the app...</p>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-500 h-2 rounded-full transition-all"
                style={{
                  width: `${Math.min(90, (retryCount + 1) * 20)}%`,
                }}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Hook to manage offline state
export const useOfflineState = () => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOffline;
};

